import { connectedFileObject, connectedFileUuid } from './connectedFileReadDomain.ts';
export const CONNECTED_MESSAGE_READ_CONTRACT = 'mirror_connected_message_read_v1' as const;
export const CONNECTED_MESSAGE_PAGE_LIMIT = 15 as const;
export const CONNECTED_MESSAGE_TTL = 5 * 60_000;
export const CONNECTED_MESSAGE_BODY_LIMIT = 20_000;
export const CONNECTED_MESSAGE_REQUEST_LIMIT = 32_000;
export type ConnectedMessageProvider = 'slack' | 'microsoft_teams';
export interface ConnectedMessageItem {
  id: string; title: string; kind: 'channel' | 'chat' | 'message'; list_id: string;
  body_text?: string; from?: string; created_at?: string; thread_id?: string;
  reply_count?: number; truncated?: boolean;
}
export interface ConnectedMessageCoverage {
  state: 'complete' | 'partial'; reason?: 'page_limited' | 'local_filter' | 'text_truncated';
  scanned: number; returned: number; limit: 15; body_truncated: boolean;
}
export interface ConnectedMessageReadRequest {
  read_contract: typeof CONNECTED_MESSAGE_READ_CONTRACT; provider: ConnectedMessageProvider;
  action: 'lists' | 'search' | 'get' | 'thread'; expected_owner_id: string;
  list_id?: string; item_id?: string; thread_id?: string; query?: string; cursor?: string;
}
export interface ConnectedMessageReadReply {
  ok: true; read_contract: typeof CONNECTED_MESSAGE_READ_CONTRACT; provider: ConnectedMessageProvider;
  action: ConnectedMessageReadRequest['action']; owner_id: string; auth_session_id: string | null; channel_read_id?: string;
  connection_id: string; account_id: string; can_write: boolean; display_name: string;
  items?: ConnectedMessageItem[]; item?: ConnectedMessageItem; next_cursor?: string | null;
  coverage: ConnectedMessageCoverage;
}
export class ConnectedMessageError extends Error {
  constructor(code: string, public status = 400, public retry_after?: number) { super(code); }
}
export const connectedMessageObject = connectedFileObject;
export const connectedMessageUuid = connectedFileUuid;
export function connectedMessageHasControls(value: string, multiline = false): boolean {
  return Array.from(value).some(c => { const n = c.charCodeAt(0); return n === 127 || n < 32 && (!multiline || ![9, 10, 13].includes(n)); });
}
export function connectedMessageCleanText(value: string, multiline = true): string {
  return Array.from(value).filter(c => !connectedMessageHasControls(c, multiline)).join('')
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/gu, '\uFFFD');
}
export function connectedMessageText(
  value: unknown, max: number, label: string, options: { multiline?: boolean; empty?: boolean } = {},
): string {
  if (typeof value !== 'string' || value.length > max || (!options.empty && !value.trim()) ||
      /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(value) ||
      Array.from(value).some(c => {
        const code = c.charCodeAt(0);
        return code === 127 || code < 32 && (!options.multiline || ![9, 10, 13].includes(code));
      })) throw new ConnectedMessageError('invalid_' + label);
  return value;
}
export function connectedMessageId(provider: ConnectedMessageProvider, value: unknown, label = 'item_id'): string {
  const id = connectedMessageText(value, 200, label);
  const valid = provider === 'slack'
    ? label === 'list_id' ? /^[CG][A-Z0-9]{1,39}$/.test(id) : /^\d{1,20}\.\d{1,20}$/.test(id)
    : /^[A-Za-z0-9_:.@-]{1,200}$/.test(id);
  if (!valid || id === '.' || id === '..') throw new ConnectedMessageError('invalid_' + label);
  return id;
}
export function connectedMessageAccountId(provider: ConnectedMessageProvider, value: unknown): string {
  const id = connectedMessageText(value, 200, 'account_id');
  if (!(provider === 'slack' ? /^T[A-Z0-9]{1,39}:[UW][A-Z0-9]{1,39}$/.test(id) : /^[A-Za-z0-9_:.@-]{1,200}$/.test(id)) || id === '.' || id === '..') {
    throw new ConnectedMessageError('provider_identity_unconfirmed', 502);
  }
  return id;
}
export function parseConnectedMessageReadRequest(raw: unknown): ConnectedMessageReadRequest {
  if (!connectedMessageObject(raw) || raw.read_contract !== CONNECTED_MESSAGE_READ_CONTRACT) {
    throw new ConnectedMessageError('invalid_read_contract');
  }
  if (raw.provider !== 'slack' && raw.provider !== 'microsoft_teams') throw new ConnectedMessageError('unsupported_provider');
  const provider = raw.provider, action = raw.action;
  if (!['lists', 'search', 'get', ...(provider === 'slack' ? ['thread'] : [])].includes(String(action))) {
    throw new ConnectedMessageError('unsupported_action');
  }
  if (!connectedMessageUuid(raw.expected_owner_id)) throw new ConnectedMessageError('invalid_owner');
  const fields = action === 'lists' ? ['query', 'cursor']
    : action === 'search' ? ['list_id', 'query', 'cursor']
    : action === 'get' ? ['list_id', 'item_id'] : ['list_id', 'thread_id', 'cursor'];
  if (Object.keys(raw).some(k => !['read_contract', 'provider', 'action', 'expected_owner_id', ...fields].includes(k))) {
    throw new ConnectedMessageError('invalid_request');
  }
  const out: ConnectedMessageReadRequest = {
    read_contract: CONNECTED_MESSAGE_READ_CONTRACT, provider,
    action: action as ConnectedMessageReadRequest['action'], expected_owner_id: raw.expected_owner_id.toLowerCase(),
  };
  if (action !== 'lists') out.list_id = connectedMessageId(provider, raw.list_id, 'list_id');
  if (action === 'get') out.item_id = connectedMessageId(provider, raw.item_id);
  if (action === 'thread') out.thread_id = connectedMessageId(provider, raw.thread_id, 'thread_id');
  if (action === 'lists' || action === 'search') {
    out.query = raw.query === undefined ? '' : connectedMessageText(raw.query, 200, 'query', { empty: true });
  }
  if (raw.cursor !== undefined) {
    out.cursor = connectedMessageText(raw.cursor, 16000, 'cursor');
    if (!/^[A-Za-z0-9_-]+$/.test(out.cursor)) throw new ConnectedMessageError('invalid_cursor');
  }
  return out;
}
